import { useEffect } from "react";
import { Field, FieldArray, Fields } from "redux-form";

import { Box } from "@braintrust/braintrust-ui-components";
import { hideLeftSidebar, showLeftSidebar } from "@js/apps/common/actions";
import {
  JobDescription,
  JobErrorSection,
  JobIntroduction,
  ProjectDetailsSectionModule,
  RequirementsSection,
  RoleSection,
} from "@js/apps/jobs/apps/create-job/components";
import { CanManageOnBehalfOfClientModule } from "@js/apps/jobs/apps/create-job/components/can-manage-job-on-behalf-of-client-module";
import { RateSectionModule } from "@js/apps/jobs/apps/create-job/components/rate-section-module";
import { SkillsSection } from "@js/apps/jobs/apps/create-job/components/skills-section";
import {
  AutoInviteContainer,
  SkipApprovalContainer,
} from "@js/apps/jobs/apps/create-job/components/summarize-module";
import {
  EditableTitleField,
  ExperienceField,
  ResumeRequirementField,
  TextFieldProposalArray,
} from "@js/apps/jobs/apps/create-job/fields";
import {
  experienceValidator,
  jobDescriptionValidator,
  jobTitleSignValidator,
  jobTitleValidator,
  projectDetailsValidator,
  skillsValidator,
} from "@js/apps/jobs/apps/create-job/validators";
import { useJobFormContext } from "@js/apps/jobs/context/job-form-context/job-form-context";
import { FieldSectionPanel } from "@js/apps/jobs/forms/fields";
import { useAppDispatch } from "@js/hooks";

import { AiInterviewSection } from "../../components/ai-interview-section";
import { JobDescriptionUpdateStatusTag } from "../../components/job-description-update-status-tag";
import { SummarizeJobModalInstance } from "../../components/summarize-module";

type CreateOrEditJobMainFieldsProps = {
  isEditForm?: boolean;
};

export const CreateOrEditJobMainFields = ({
  isEditForm,
}: CreateOrEditJobMainFieldsProps) => {
  const dispatch = useAppDispatch();
  const { jobToEdit } = useJobFormContext();

  useEffect(() => {
    dispatch(hideLeftSidebar());
    return () => {
      dispatch(showLeftSidebar());
    };
  }, [dispatch]);

  return (
    <Box sx={{ maxWidth: "787px" }}>
      <CanManageOnBehalfOfClientModule badgeOnly={true} />
      <JobDescriptionUpdateStatusTag />
      <SummarizeJobModalInstance />
      <Box id="category" sx={{ mb: 3 }}>
        <Field name="role" component={RoleSection} isEditForm={isEditForm} />
      </Box>

      {isEditForm && <AutoInviteContainer jobToEdit />}
      {isEditForm && (
        <Box sx={{ mt: 2 }}>
          <SkipApprovalContainer />
        </Box>
      )}

      <Box id="job-title" sx={{ my: 3 }}>
        <Field
          id="job-title"
          name="title"
          placeholder="Enter job title here"
          validate={[jobTitleValidator, jobTitleSignValidator]}
          component={EditableTitleField}
        />
      </Box>
      <Field name="" component={JobErrorSection} />
      <Field
        name="description"
        component={JobDescription}
        validate={[jobDescriptionValidator]}
      />
      <Fields
        names={[
          "contract_type",
          "deadline",
          "start_date",
          "expected_hours_per_week",
          "location",
          "timezone",
          "timezone_overlap",
        ]}
        component={ProjectDetailsSectionModule}
        validate={{ contract_type: [projectDetailsValidator] }}
        isEditForm={isEditForm}
      />

      <Fields
        names={["experience_level", "role"]}
        component={ExperienceField}
        validate={{ experience_level: [experienceValidator] }}
      />

      <Fields
        names={["new_skills", "top_skills"]}
        component={SkillsSection}
        validate={[skillsValidator]}
      />

      <RateSectionModule />

      <Field name="is_resume_required" component={ResumeRequirementField} />

      {jobToEdit?.requirements && (
        <Field name="requirements" component={RequirementsSection} />
      )}

      {jobToEdit?.introduction && (
        <Field name="introduction" component={JobIntroduction} />
      )}

      <FieldSectionPanel
        title="Application questions (optional)"
        description="Talent is prompted to answer these when submitting an application"
      >
        <FieldArray
          name="new_application_questions"
          component={TextFieldProposalArray}
        />
      </FieldSectionPanel>

      <Field name="is_ai_interview_enabled" component={AiInterviewSection} />
    </Box>
  );
};
